import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Timeline from 'antd/lib/timeline';
import Tooltip from 'antd/lib/tooltip';
import { ArrowRightOutlined } from '@ant-design/icons';
import { dayjs } from '@/extendedDayjs';
import { formatDateWithTimezone } from '../../timeHelpers';
import { antGrey } from '../styles/globalStyles';
import {
    FeatureFlagChange,
    FeatureFlagChangeEvent,
    FeatureFlagChangeType,
    FeatureFlagTimeSeriesDatum,
} from '../../types';

// The rollout step already holds the release's full time series, so derive a flag's history from it rather than
// relying on the (cached, incrementally built) flag objects inside each datum.
export const changesFromTimeSeries = (flagId: string, datums: FeatureFlagTimeSeriesDatum[]): FeatureFlagChangeEvent[] =>
    datums
        .filter((datum) => datum.flagAfter?.id === flagId || datum.flagBefore?.id === flagId)
        .filter((datum) => datum.diff?.length > 0)
        .map((datum) => ({ timestamp: datum.timestamp, diff: datum.diff }))
        .sort((a, b) => dayjs.utc(b.timestamp).valueOf() - dayjs.utc(a.timestamp).valueOf());

const PATH_SEGMENT_LABELS: Record<string, string> = {
    status: 'Status',
    enabledAt: 'Enabled at',
    rolloutPercentage: 'Rollout',
    minimumVersion: 'Minimum version',
    maximumVersion: 'Maximum version',
    equalVersions: 'Target versions',
    platforms: 'Platforms',
    rules: 'Rule',
    variations: 'Variation',
    audienceConditions: 'Audiences',
    conditions: 'Conditions',
    variables: 'Variables',
    weight: 'weight',
    isDefault: 'default',
    predicate: 'predicate',
    values: 'values',
    negate: 'negated',
    value: 'value',
    type: 'type',
};

// Diff paths look like ["rules", "<rule id>", "variations", "<variant key>", "weight"]; identifiers keep their
// raw value so the user can recognize them, known field names get a label.
export const describeChangePath = (path: string[]): string => {
    return path
        .reduce<string[]>((segments, segment) => segments.concat(segment.split('/')), [])
        .map((segment) => PATH_SEGMENT_LABELS[segment] ?? segment)
        .join(' › ');
};

const isPercentagePath = (path: string[]): boolean => path.includes('rolloutPercentage');

export const formatChangeValue = (value: unknown, path: string[]): string => {
    if (value === null || value === undefined || value === '') {
        return 'None';
    }
    if (typeof value === 'number' && isPercentagePath(path)) {
        return `${Math.round(value * 1000) / 10}%`;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    return JSON.stringify(value);
};

const changeTypeVerb = (type: FeatureFlagChangeType): string | null => {
    switch (type) {
        case FeatureFlagChangeType.Create:
            return 'Added';
        case FeatureFlagChangeType.Delete:
            return 'Removed';
        default:
            return null;
    }
};

export const FeatureFlagChangeRow: React.FC<{ change: FeatureFlagChange }> = ({ change }) => {
    const verb = changeTypeVerb(change.type);
    const from = formatChangeValue(change.from, change.path);
    const to = formatChangeValue(change.to, change.path);

    const label = describeChangePath(change.path);

    // Every piece can truncate so long rule ids or JSON values never widen the drawer
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 8,
                fontSize: 13,
                lineHeight: '20px',
                minWidth: 0,
                maxWidth: '100%',
            }}
        >
            <Text
                style={{ color: antGrey.grey7, flexShrink: 1, minWidth: 0, maxWidth: '55%' }}
                ellipsis={{ tooltip: label }}
            >
                {label}
            </Text>
            {verb ?
                <Text
                    style={{ color: antGrey.grey8, flexShrink: 1, minWidth: 0 }}
                    ellipsis={{ tooltip: `${verb} ${to !== 'None' ? to : from}` }}
                >
                    {verb} {to !== 'None' ? to : from}
                </Text>
            :   <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0, flexShrink: 1 }}>
                    <Text style={{ color: antGrey.grey6, minWidth: 0 }} ellipsis={{ tooltip: from }}>
                        {from}
                    </Text>
                    <ArrowRightOutlined style={{ fontSize: 10, color: antGrey.grey6, flexShrink: 0 }} />
                    <Text style={{ color: antGrey.grey8, fontWeight: 600, minWidth: 0 }} ellipsis={{ tooltip: to }}>
                        {to}
                    </Text>
                </span>
            }
        </div>
    );
};

export const FeatureFlagChangeList: React.FC<{ changes: FeatureFlagChange[] }> = ({ changes }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {changes.map((change, index) => (
            <FeatureFlagChangeRow key={`${change.path.join('.')}-${index}`} change={change} />
        ))}
    </div>
);

interface FeatureFlagChangesTimelineProps {
    changes: FeatureFlagChangeEvent[];
    timezone: string;
    use24HourTimeFormat: boolean;
}

export const FeatureFlagChangesTimeline: React.FC<FeatureFlagChangesTimelineProps> = ({
    changes,
    timezone,
    use24HourTimeFormat,
}) => {
    return (
        <Timeline
            style={{ marginTop: 24 }}
            items={changes.map((event) => {
                const detectedAt = dayjs(event.timestamp);
                return {
                    color: 'gray',
                    children: (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <Tooltip
                                title={formatDateWithTimezone(
                                    detectedAt,
                                    'fullWithTimezone',
                                    timezone,
                                    use24HourTimeFormat,
                                )}
                            >
                                <Text style={{ fontSize: 12, color: antGrey.grey7 }}>
                                    {formatDateWithTimezone(
                                        detectedAt,
                                        'shortMonthDateWithTimeZone',
                                        timezone,
                                        use24HourTimeFormat,
                                    )}
                                </Text>
                            </Tooltip>
                            <FeatureFlagChangeList changes={event.diff} />
                        </div>
                    ),
                };
            })}
        />
    );
};
